import React from "react";
import { Helmet } from "react-helmet-async";
import {
  Container,
  Typography,
  Grid,
  Box,
  Paper,
  useTheme,
  Button,
} from "@mui/material";
import { LocationOn, Phone, Email, ArrowForward } from "@mui/icons-material";

const ContactUs = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  return (
    <>
      <Helmet>
        <title>Contact Us | DeedMed</title>
        <meta
          name="description"
          content="Contact DeedMed for appointments, inquiries, and support. Find our address, phone, email, and send us a message."
        />
        <meta property="og:title" content="Contact Us | DeedMed" />
        <meta
          property="og:description"
          content="Contact DeedMed for appointments, inquiries, and support. Find our address, phone, email, and send us a message."
        />
        <meta property="og:url" content="https://deedmed.lk/contact/" />
        <meta property="og:type" content="website" />
      </Helmet>

      <Box sx={{ bgcolor: "background.default", py: { xs: 4, md: 10 } }}>
        <Container maxWidth="lg">
          <Typography variant="h1" gutterBottom color="primary.dark">
            Contact Us
          </Typography>
          <Typography variant="h5" sx={{ mb: 4 }}>
            We're here to help! Reach out to us for appointments, inquiries, or
            support.
          </Typography>

          <Grid container spacing={4} justifyContent="center">
            {/* Contact Information Card */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={4}
                sx={{
                  p: 5,
                  bgcolor: isDarkMode ? "grey.800" : "background.paper",
                  border: `2px solid ${theme.palette.primary.main}`,
                }}
              >
                <Typography variant="h3" gutterBottom color="primary.main">
                  Get in Touch
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ mb: 3, color: "text.secondary" }}
                >
                  Ready to connect? Here's how you can reach us
                </Typography>

                {/* Address */}
                <Box sx={{ display: "flex", alignItems: "flex-start", mb: 3 }}>
                  <LocationOn
                    sx={{
                      color: theme.palette.primary.main,
                      mt: 0.5,
                      mr: 2,
                    }}
                  />
                  <Box>
                    <Typography
                      variant="h6"
                      color="text.primary"
                      sx={{ mb: 1 }}
                    >
                      Address
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      2nd Floor, Paramount Tower,
                      <br />
                      192/10, 9th Lane, Nawala Road, Nawala
                      <br />
                      Sri Lanka
                    </Typography>
                  </Box>
                </Box>

                {/* Phone */}
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <Phone
                    sx={{
                      color: theme.palette.primary.main,
                      mr: 2,
                    }}
                  />
                  <Box>
                    <Typography
                      variant="h6"
                      color="text.primary"
                      sx={{ mb: 1 }}
                    >
                      Phone
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      070 7 665522
                    </Typography>
                  </Box>
                </Box>

                {/* Email */}
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <Email
                    sx={{
                      color: theme.palette.primary.main,
                      mr: 2,
                    }}
                  />
                  <Box>
                    <Typography
                      variant="h6"
                      color="text.primary"
                      sx={{ mb: 1 }}
                    >
                      Email
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      info@deedmed.lk
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ textAlign: "center", mt: 4 }}>
                  <Button
                    variant="contained"
                    size="large"
                    color="primary"
                    endIcon={<ArrowForward />}
                    sx={{ px: 4, py: 1.5 }}
                    href="mailto:info@deedmed.lk"
                  >
                    Send us an Email
                  </Button>
                </Box>
              </Paper>
            </Grid>

            {/* Map Card */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={4}
                sx={{
                  p: 5,
                  bgcolor: isDarkMode ? "grey.800" : "background.paper",
                  border: `2px solid ${theme.palette.secondary.main}`,
                }}
              >
                <Typography variant="h3" gutterBottom color="secondary.main">
                  Find Us
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ mb: 3, color: "text.secondary" }}
                >
                  Visit us at our convenient Nawala location
                </Typography>

                <Box
                  sx={{
                    borderRadius: 2,
                    overflow: "hidden",
                    boxShadow: 2,
                    mb: 3,
                  }}
                >
                  <iframe
                    title="DeedMed Location"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3960.960964073812!2d79.8772223147727!3d6.897071995018846!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ae2597e2e2e2e2e%3A0x7e2e6e6e6e6e6e6e!2sParamount%20Tower%2C%20Nawala%20Rd%2C%20Nawala!5e0!3m2!1sen!2slk!4v1718000000000!5m2!1sen!2slk"
                    width="100%"
                    height="250"
                    style={{ border: 0 }}
                    allowFullScreen={false}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </Box>

                <Box sx={{ textAlign: "center" }}>
                  <Button
                    variant="contained"
                    size="large"
                    color="secondary"
                    endIcon={<ArrowForward />}
                    sx={{ px: 4, py: 1.5 }}
                    href="https://maps.google.com/?q=Paramount+Tower,+Nawala+Road,+Nawala,+Sri+Lanka"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Get Directions
                  </Button>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default ContactUs;
